import { useState, useEffect } from 'react';
import { Box, CircularProgress, Grid, TextField, Stack, Button } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import PickerField from '../../../components/PickerField';
import FormDialog from '../../../components/FormDialog';
import { useNotifier } from '../../../components/Notifier';
import { useLanguage } from '../../../utils/i18n';
import { getCountryById, createCountry, updateCountry } from '../../../stores/countryStore';
import { useCompanyDataList } from '../../../stores/companyStore';
import usePickerPagination from '../../../hooks/usePickerPagination';
import { formatUpperCase } from '../../../utils/formatTitleCase';

const MasterCountryAddEdit = ({ open, id, onClose, onSuccess }) => {
  const [initialValues, setInitialValues] = useState({
    company: '',
    name: '',
    code: ''
  });
  const { t } = useLanguage();

  const [loading, setLoading] = useState(false);
  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });

  const companyOptions = companyPickerParams.options;
  const notify = useNotifier();

  useEffect(() => {
    if (!open) return;

    let mounted = true;
    const fetchInitial = async () => {
      setLoading(true);
      try {
        if (id) {
          const data = await getCountryById(id);
          if (!mounted) return;
          if (data) {
            setInitialValues({
              company: data.company ?? data.company_id ?? '',
              name: data.name ?? '',
              code: data.code ?? ''
            });
          }
        } else {
          let defaultCompany = '';
          const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
          if (stored) {
            const match = companyOptions.find((c) => String(c.value) === String(stored));
            if (match) defaultCompany = String(match.value);
          }
          setInitialValues({
            company: defaultCompany,
            name: '',
            code: ''
          });
        }
      } catch (err) {
        console.error('Failed to fetch country:', err);
        notify({ message: t('notify.failedLoadData', 'Failed to load data'), severity: 'error' });
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchInitial();

    return () => {
      mounted = false;
    };
  }, [id, open, companyOptions, notify, t]);

  const validationSchema = Yup.object().shape({
    company: Yup.mixed().required(t('validation.companyRequired', 'Company is required')),
    name: Yup.string().required(t('validation.nameRequired', 'Name is required')),
    code: Yup.string().required(t('validation.codeRequired', 'Code is required')).max(3, 'Max 3 characters')
  });

  const handleClose = () => {
    formik.resetForm();
    if (onClose) onClose();
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          company: Number(values.company),
          name: formatUpperCase(values.name),
          code: values.code
        };
        let response;
        if (id) {
          response = await updateCountry(id, payload);
          notify({ message: t('notify.update', 'Updated successfully'), severity: 'success' });
        } else {
          response = await createCountry(payload);
          notify({ message: t('notify.create', 'Created successfully'), severity: 'success' });
        }
        if (response?.code === 400 || response?.status === false || response?.error) {
          throw response;
        }
        formik.resetForm();
        if (onClose) onClose();
      } catch (err) {
        console.error('Submit error:', err);
        const errorMessage = err?.response?.data?.message || err?.data?.message || err?.message || 'Gagal menyimpan data';
        notify({ message: errorMessage, severity: 'error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  const dialogActions = (
    <>
      <Button onClick={handleClose} disabled={formik.isSubmitting}>
        {t('buttonTitle.cancel', 'Cancel')}
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={formik.handleSubmit}
        disabled={formik.isSubmitting || loading}
        startIcon={formik.isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
        sx={{
          textTransform: 'none',
          borderRadius: 1,
          px: 3,
          fontWeight: 700,
          boxShadow: '0 8px 20px rgba(25,118,210,0.12)'
        }}
      >
        {formik.isSubmitting ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
      </Button>
    </>
  );

  return (
    <FormDialog
      open={open}
      onClose={handleClose}
      title={
        id
          ? t('general.edit', 'Edit') + ' ' + t('masterCountry.title', 'Master Country')
          : t('general.add', 'Add') + ' ' + t('masterCountry.title', 'Master Country')
      }
      maxWidth="xs"
      fullHeight={false}
      actions={dialogActions}
    >
      <Box sx={{ width: '100%', maxWidth: 920 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Grid spacing={3}>
              <Grid item xs={12} md={6}>
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <PickerField
                    label={t('general.company', 'Company') + ' *'}
                    value={formik.values.company}
                    {...companyPickerParams}
                    options={companyOptions}
                    onChange={(opt) => {
                      const val = typeof opt === 'object' ? opt?.value : opt;
                      const selected = companyOptions.find((c) => String(c.value) === String(val)) ?? null;
                      const selectedId = selected ? selected.value : (val ?? null);
                      formik.setFieldValue('company', selectedId);
                    }}
                    dialogTitle={t('masterCountry.selectCompany', 'Select Company')}
                    error={formik.touched.company && !!formik.errors.company}
                    helperText={formik.touched.company && formik.errors.company}
                  />

                  <TextField
                    fullWidth
                    size="medium"
                    id="name"
                    name="name"
                    label={t('general.name', 'Name') + ' *'}
                    value={formik.values.name}
                    onChange={(e) => {
                      formik.setFieldValue('name', formatUpperCase(e.target.value));
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.touched.name && !!formik.errors.name}
                    helperText={formik.touched.name && formik.errors.name}
                  />

                  <TextField
                    fullWidth
                    size="medium"
                    id="code"
                    name="code"
                    label={t('masterUser.code', 'Code') + ' *'}
                    value={formik.values.code}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.code && !!formik.errors.code}
                    helperText={formik.touched.code && formik.errors.code}
                  />
                </Stack>
              </Grid>
            </Grid>
          </form>
        )}
      </Box>
    </FormDialog>
  );
};

export default MasterCountryAddEdit;
